import { resolveSWRCashMode, SWR_CASH_MODES, SWR_CASH_DEFAULTS } from './swrCashModel';
import { getVerifiedSWRPortfolio } from './swrPortfolioCalibration';
import { normalizeSWRContributions } from './swrContributions';
import { normalizeSWRHistoricalPortfolio, prepareSWRHistoricalPortfolio } from './swrHistoricalPortfolio';
import { isSWRFlowTaxPortfolio, createSWRSimpleTaxProfile, getSWRSimpleTaxRate } from './swrTaxModel';
import { normalizeSWRWithdrawalStrategy, normalizeSWRWithdrawalCustomization, getSWRWithdrawalStrategyCapabilities } from './swrWithdrawalStrategies';

export const SWR_MODEL_VERSION = 'swr-strategy-overlays-v18';
export const SWR_SAFE_WITHDRAWAL_RISK_METRICS = Object.freeze(['capital-depletion', 'minimum-income-shortfall']);

// Applied at every public calculation entry point, including imported scenarios.
export function normalizeSWRParams(input) {
  if (!input || typeof input !== 'object') throw new Error('Parametri SWR mancanti.');
  const p = { ...input };
  p.withdrawalStrategy = normalizeSWRWithdrawalStrategy(p.withdrawalStrategy);
  p.withdrawalCustomization = normalizeSWRWithdrawalCustomization(p.withdrawalCustomization);
  // Retired flags never activate spending rules, including imported JSON.
  delete p.cashBufferGuytonKlingerEnabled;
  delete p.cashBufferGuytonKlingerMinExpensePct;
  delete p.dynamicCashBufferGuardrailMode;
  const capabilities = getSWRWithdrawalStrategyCapabilities(p.withdrawalStrategy);
  if (!capabilities.allowsGuardrails) {
    Object.assign(p, { withdrawalPolicy: 'fixed', guardrailsEnabled: false,
      guardrailMinEnabled: false, guardrailMaxEnabled: false,
      guardrailMinPct: null, guardrailMaxPct: null,
      minRealWithdrawalYear0: null, maxRealWithdrawalYear0: null });
  }
  if (!capabilities.allowsCashBuffer || (p.withdrawalStrategy.id !== 'personalized' && !p.dynamicCashBufferEnabled)) {
    Object.assign(p, { dynamicCashBufferEnabled: false, cashBufferMonths: 12, cashBufferFunding: 'existing-cash' });
  }
  if (p.withdrawalStrategy.id !== 'personalized' && capabilities.allowsGuardrails) {
    // Named strategies only accept explicit percentage bounds, never legacy
    // absolute overrides or hidden drafts of the personalised wealth rule.
    const enabled = p.guardrailsEnabled === true || p.withdrawalPolicy === 'guardrails';
    p.minRealWithdrawalYear0 = null;
    p.maxRealWithdrawalYear0 = null;
    if (!enabled) Object.assign(p, { withdrawalPolicy: 'fixed', guardrailsEnabled: false,
      guardrailMinEnabled: false, guardrailMaxEnabled: false, guardrailMinPct: null, guardrailMaxPct: null });
  }
  const historical = normalizeSWRHistoricalPortfolio(p.historicalPortfolio);
  if (historical) {
    if (p.swrConfigurationMode === 'standard') throw new Error('Il portafoglio custom per asset class è disponibile nei livelli Intermedio e Avanzato.');
    if (p.maxSingleYearDrawdown != null) throw new Error('Il portafoglio custom non ammette un limite artificiale alle perdite. Rimuovi il limite per usarlo.');
    p.historicalPortfolio = historical;
    const prepared = prepareSWRHistoricalPortfolio(historical, p);
    p.expectedReturn = prepared.summary.appliedStatistics.arithmeticMean;
    p.annualVolatility = prepared.summary.appliedStatistics.annualVolatility;
    p.inflationRate = prepared.summary.effectiveInflation;
    p.portfolioPreset = 'custom';
    p.modelPortfolio = null;
    p.returnParameterUncertaintyEnabled = false;
    p.customReturnParameterUncertaintyEnabled = false;
    p.returnParameterUncertaintyMode = 'historical-block-bootstrap';
    p.macroMode = 'historical-joint-v1';
  }
  if (p.valuationDate != null && (!/^\d{4}-\d{2}-\d{2}$/.test(p.valuationDate)
    || !Number.isFinite(Date.parse(p.valuationDate))
    || new Date(p.valuationDate).toISOString().slice(0, 10) !== p.valuationDate)) {
    throw new Error('Data di riferimento degli euro non valida.');
  }
  if (isSWRFlowTaxPortfolio(p) && !p.valuationDate) throw new Error('La fiscalità per strumento richiede una data di riferimento per il calendario civile.');
  const number = (key, fallback, min, max, integer = false, exclusiveMin = false) => {
    const value = p[key] == null ? fallback : p[key];
    if (typeof value !== 'number' || !Number.isFinite(value)
      || (exclusiveMin ? value <= min : value < min) || value > max
      || (integer && !Number.isInteger(value))) {
      throw new Error(`Parametro SWR non valido: ${key}. Controlla valore e unità.`);
    }
    p[key] = value;
  };
  number('initialCapital', undefined, 0, 1e15, false, true);
  number('yearsToSimulate', 40, 1, 100, true);
  p.capitalContributions = normalizeSWRContributions(p.capitalContributions, p.yearsToSimulate);
  number('numberOfSimulations', 5000, 1, 25000, true);
  number('expectedReturn', 0, -1, 5, false, true);
  number('annualVolatility', 0, 0, 5);
  number('inflationRate', 0.03, -1, 1, false, true);
  number('taxRate', 0.26, 0, 1, false);
  if (p.simpleTaxProfile) {
    const profile = createSWRSimpleTaxProfile(p.modelPortfolio?.equityPct);
    if (historical || !getVerifiedSWRPortfolio(p) || Object.keys(profile).some(k => p.simpleTaxProfile[k] !== profile[k])
      || Math.abs(p.taxRate - getSWRSimpleTaxRate(profile.equityPct / 100)) > 1e-12) {
      throw new Error('La proxy fiscale del preset non corrisponde al registro congelato o all’aliquota applicata.');
    }
    p.simpleTaxProfile = profile;
  }
  if (p.taxRate === 1) throw new Error('L’aliquota SWR deve essere inferiore al 100% per finanziare un prelievo netto.');
  number('initialGainFraction', 0, 0, 1);
  number('realWithdrawalYear0', 0, 0, 1e15);
  number('annualPortfolioCostRate', 0, 0, 0.5);
  number('annualWealthTaxRate', 0, 0, 0.5);
  if (p.withdrawalStrategy.id !== 'personalized' && !p.dynamicCashBufferEnabled) {
    // Hidden inactive assumptions cannot block a run or change its identity.
    Object.assign(p, { cashReturnMode: 'inflation-net', cashAnnualReturn: p.inflationRate,
      cashLongRunReturn: SWR_CASH_DEFAULTS.cashLongRunReturn,
      cashRateHalfLifeMonths: SWR_CASH_DEFAULTS.cashRateHalfLifeMonths,
      cashInflationPassThrough: SWR_CASH_DEFAULTS.cashInflationPassThrough, cashTaxRate: 0 });
  }
  p.cashReturnMode = resolveSWRCashMode(p);
  if (!SWR_CASH_MODES.includes(p.cashReturnMode)) throw new Error('Modello di rendimento del buffer non valido.');
  number('cashAnnualReturn', SWR_CASH_DEFAULTS.cashAnnualReturn, -1, 1, false, true);
  number('cashLongRunReturn', SWR_CASH_DEFAULTS.cashLongRunReturn, -1, 1, false, true);
  number('cashRateHalfLifeMonths', SWR_CASH_DEFAULTS.cashRateHalfLifeMonths, 1, 120, true);
  number('cashInflationPassThrough', SWR_CASH_DEFAULTS.cashInflationPassThrough, 0, 1);
  number('cashTaxRate', 0.26, 0, 1);
  number('minimumIncomeCoverage', 1, 0, 1, false, true);
  p.safeWithdrawalRiskMetric = p.safeWithdrawalRiskMetric ?? 'capital-depletion';
  if (!SWR_SAFE_WITHDRAWAL_RISK_METRICS.includes(p.safeWithdrawalRiskMetric)) {
    throw new Error('Criterio di rischio delle card SWR non valido. Scegli esaurimento capitale o reddito minimo.');
  }
  for (const field of ['guardrailMinPct', 'guardrailMaxPct']) {
    if (p[field] != null) number(field, 0.2, 0, 1);
  }
  if (p.cashBufferMonths != null) number('cashBufferMonths', 12, 6, 36, true);
  for (const field of ['minRealWithdrawalYear0', 'maxRealWithdrawalYear0', 'withdrawalPhase1Annual', 'withdrawalPhase2Annual']) {
    if (p[field] != null) number(field, 0, 0, 1e15);
  }
  for (const field of ['withdrawalPhase1Years', 'withdrawalPhase2Years']) {
    if (p[field] != null) number(field, 0, 0, 100, true);
  }
  if (p.minRealWithdrawalYear0 != null && p.maxRealWithdrawalYear0 != null && p.minRealWithdrawalYear0 > p.maxRealWithdrawalYear0) {
    throw new Error('Il prelievo minimo non può superare il massimo.');
  }
  for (const field of ['dynamicCashBufferEnabled', 'guardrailsEnabled', 'guardrailMinEnabled', 'guardrailMaxEnabled', 'futurePlanningEnabled', 'returnParameterUncertaintyEnabled', 'customReturnParameterUncertaintyEnabled']) {
    if (p[field] != null && typeof p[field] !== 'boolean') throw new Error(`Opzione SWR non valida: ${field}.`);
  }
  if (p.withdrawalPolicy != null && !['fixed', 'guardrails'].includes(p.withdrawalPolicy)) throw new Error('Politica di prelievo SWR non valida.');
  p.cashBufferFunding = p.cashBufferFunding ?? 'existing-cash';
  if (!['existing-cash', 'portfolio-sale'].includes(p.cashBufferFunding)) throw new Error('Origine della riserva di liquidità non valida.');
  p.macroMode = p.macroMode ?? 'fixed';
  if (!['fixed', 'stochastic-euro-v1', ...(historical ? ['historical-joint-v1'] : [])].includes(p.macroMode)) throw new Error('Modalità macro SWR non valida.');
  if (p.cashReturnMode === 'macro-proxy' && !['stochastic-euro-v1', 'historical-joint-v1'].includes(p.macroMode)) throw new Error('La proxy dei depositi richiede inflazione stocastica.');
  const future = p.planningMode === 'future' || p.futurePlanningEnabled;
  number('yearsUntilStart', 0, 0, 100, true);
  const expectedFactor = future ? Math.pow(1 + p.inflationRate, p.yearsUntilStart) : 1;
  number('preSimulationInflationFactor', expectedFactor, 0, 1e30, false, true);
  if (Math.abs(p.preSimulationInflationFactor / expectedFactor - 1) > 1e-8) {
    throw new Error('L’indicizzazione iniziale non corrisponde all’inflazione e agli anni di attesa. Rivedi i parametri del piano senza rivalutare due volte i prelievi.');
  }
  if (p.realWithdrawalYear0Today != null) number('realWithdrawalYear0Today', 0, 0, 1e15);
  if (p.withdrawalPeriods != null) {
    if (!Array.isArray(p.withdrawalPeriods) || p.withdrawalPeriods.length > 4) throw new Error('Il piano SWR ammette al massimo quattro periodi.');
    p.withdrawalPeriods = p.withdrawalPeriods.map((period) => {
      const amount = period?.annualWithdrawal ?? (period?.monthlyWithdrawal != null ? period.monthlyWithdrawal * 12 : p.realWithdrawalYear0);
      if (!Number.isInteger(period?.years) || period.years < 1 || !Number.isFinite(amount) || amount < 0 || amount > 1e15) {
        throw new Error('Periodo SWR non valido: verifica durata e prelievo.');
      }
      return { ...period, annualWithdrawal: amount, monthlyWithdrawal: amount / 12 };
    });
  }
  if (p.maxSingleYearDrawdown != null) number('maxSingleYearDrawdown', null, 0, 1, false, true);
  // A cap is an artificial scenario, never an investible protection in safe-income cards.
  p.artificialReturnCap = p.maxSingleYearDrawdown != null;
  if ((p.portfolioPreset || p.modelPortfolio?.id || '').startsWith('etf-eq') && !getVerifiedSWRPortfolio(p)) {
    throw new Error('Il preset ETF non corrisponde alla calibrazione disponibile. Riseleziona il mix oppure usa valori Custom.');
  }
  return p;
}

export function probabilityInterval(failures, trials) {
  if (!Number.isInteger(trials) || trials <= 0) return { estimate: null, lower: null, upper: null, trials: 0, failures: 0 };
  const estimate = failures / trials;
  const z = 1.959963984540054;
  const denominator = 1 + z * z / trials;
  const centre = (estimate + z * z / (2 * trials)) / denominator;
  const half = z * Math.sqrt(estimate * (1 - estimate) / trials + z * z / (4 * trials * trials)) / denominator;
  return { estimate, lower: Math.max(0, centre - half), upper: Math.min(1, centre + half), trials, failures, confidence: 0.95, method: 'wilson' };
}
